import React from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "@/lib/modal";
import { AlertTriangle, HelpCircle } from "lucide-react";

/**
 * Reusable confirmation modal — replaces native window.confirm with a
 * branded overlay consistent with Accounting void / expense modals.
 */
export default function ConfirmDialog({
  open,
  title,
  description,
  children,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  variant = "default", // default | danger | primary
  loading = false,
  testId = "confirm-dialog",
}) {
  if (!open) return null;

  const confirmClass = variant === "danger"
    ? "bg-rose-600 hover:bg-rose-700 text-white"
    : variant === "primary"
      ? "btn-gold"
      : "border border-[var(--border)] bg-[var(--surface)] text-[var(--text)] hover:border-[var(--gold)]";

  const Icon = variant === "danger" ? AlertTriangle : HelpCircle;
  const iconWrapClass = variant === "danger"
    ? "bg-rose-500/15 text-rose-400"
    : "bg-[var(--gold-tint)] text-[var(--gold)]";

  return (
    <div
      className="fixed inset-0 z-50 bg-[#2B1C1C]/40 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => onBackdropMouseDown(e, onCancel)}
      data-testid={testId}
    >
      <div
        className="card-surface p-6 w-full max-w-md space-y-4"
        onMouseDown={onModalPanelMouseDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${testId}-title`}
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${iconWrapClass}`}>
            <Icon size={20}/>
          </div>
          <div className="min-w-0 flex-1">
            <h3 id={`${testId}-title`} className="font-heading text-lg text-[var(--text)]">{title}</h3>
            {description && (
              <p className="text-sm text-[var(--text-muted)] mt-1 whitespace-pre-line">{description}</p>
            )}
          </div>
        </div>
        {children}
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 px-4 py-2 rounded-md border border-[var(--border)] text-sm disabled:opacity-50"
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50 ${confirmClass}`}
            data-testid={`${testId}-confirm`}
          >
            {loading ? "…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
